import { useCallback, useEffect, useState } from 'react';
import { Button, Input, Modal } from '@cuito/ui';
import { readErrorMeta } from '@cuito/api-client';
import type { ProductDto, PromotionSchemeDto } from '@cuito/types';
import { centsFromYuanOrZero } from '@cuito/ui';
import { catalogApi } from '../api/catalog';
import {
  PRICE_UNAVAILABLE_TEXT,
  formatCents,
  formatServerYuan,
  validateSchemeForm,
  yuanToCents,
} from '../lib/schemeForm';
import { useAuthStore } from '../stores/authStore';

const PAGE_SIZE = 20;

/** 商品下拉的单页上限（契约 §1.3：`pageSize` 取值 1–100） */
const PICKER_PAGE_SIZE = 100;

/** 方案组成编辑器里的一行；`productId` 为空串表示这一行还没选商品 */
interface SchemeRow {
  productId: string;
  quantity: number;
}

const EMPTY_ROW: SchemeRow = { productId: '', quantity: 1 };

/**
 * 促销方案管理（`05-ui-design-system.md` §3.2 `/promotion-schemes`）。
 *
 * **方案不是商品、不持有库存**：本页刻意不出现「方案库存」「可售套数」——
 * 方案在当前地点能卖几份，由方案内各单品的可用量现场算出，只在货架与购物车里展示。
 * 因此这里也没有「组合」「套装」之类的字段（`README.md` §3.3）。
 */
export default function PromotionSchemesPage() {
  const canWrite = useAuthStore((s) => s.hasPermission('product:write'));

  const [schemes, setSchemes] = useState<PromotionSchemeDto[]>([]);
  const [products, setProducts] = useState<ProductDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<PromotionSchemeDto | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priceInput, setPriceInput] = useState('');
  const [active, setActive] = useState(true);
  const [rows, setRows] = useState<SchemeRow[]>([EMPTY_ROW]);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const res = await catalogApi.listSchemes({ page: 1, pageSize: PAGE_SIZE });
      setSchemes(res.items);
    } catch (e) {
      setError(readErrorMeta(e)?.status === 403 ? '无权查看该社团的促销方案' : '加载失败，请重试');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /** 编辑器需要「本社团商品」做下拉；单独拉一次，不随列表刷新 */
  useEffect(() => {
    void (async () => {
      try {
        const res = await catalogApi.listProducts({ page: 1, pageSize: PICKER_PAGE_SIZE });
        setProducts(res.items);
      } catch {
        // 商品下拉拉不到不阻塞方案列表：提交时后端仍会校验 productId
        setProducts([]);
      }
    })();
  }, []);

  const priceOf = useCallback(
    (productId: string): number => {
      const product = products.find((p) => p.id === productId);
      if (!product) return 0;
      // 算术层（不是展示层）：坏值折成 0 用 `centsFromYuanOrZero`，这是它唯一的用途
      return centsFromYuanOrZero(product.price);
    },
    [products],
  );

  // 还没选商品的行不参与校验：否则新增一行就会撞上「同一商品出现两次」（两行都是空串）
  const filledRows = rows.filter((row) => row.productId !== '');
  const formItems = filledRows.map((row) => ({
    productId: row.productId,
    quantity: row.quantity,
    priceCents: priceOf(row.productId),
  }));

  const priceCents = yuanToCents(priceInput);
  const preview = validateSchemeForm({ items: formItems, priceCents: priceCents ?? 0 });
  const itemsError = preview.errors.items;
  const priceError = priceCents === null ? '请填写方案价' : preview.errors.priceCents;

  const openCreate = (): void => {
    setEditing(null);
    setTitle('');
    setDescription('');
    setPriceInput('');
    setActive(true);
    setRows([{ ...EMPTY_ROW }]);
    setFormError(null);
    setShowForm(true);
  };

  const openEdit = (scheme: PromotionSchemeDto): void => {
    setEditing(scheme);
    setTitle(scheme.title);
    setDescription(scheme.description ?? '');
    // 分 → 元：方案价回填给输入框，用户改完再一次性转回整数分
    setPriceInput((scheme.priceCents / 100).toFixed(2));
    setActive(scheme.active);
    setRows(
      scheme.items.length === 0
        ? [{ ...EMPTY_ROW }]
        : scheme.items.map((item) => ({ productId: item.productId, quantity: item.quantity })),
    );
    setFormError(null);
    setShowForm(true);
  };

  const submit = async (): Promise<void> => {
    if (title.trim().length < 2) {
      setFormError('请填写方案标题');
      return;
    }
    if (priceCents === null) {
      setFormError('请填写方案价');
      return;
    }
    if (!preview.ok) {
      setFormError(itemsError ?? priceError ?? '保存失败，请重试');
      return;
    }

    const body = {
      type: 'COMBO',
      title: title.trim(),
      description: description.trim() === '' ? undefined : description.trim(),
      priceCents,
      active,
      items: filledRows.map((row) => ({ productId: row.productId, quantity: row.quantity })),
    };

    setSaving(true);
    setFormError(null);
    try {
      if (editing) {
        await catalogApi.updateScheme(editing.id, body);
      } else {
        await catalogApi.createScheme(body);
      }
      setShowForm(false);
      await load();
    } catch {
      setFormError('保存失败，请重试');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (scheme: PromotionSchemeDto): Promise<void> => {
    try {
      await catalogApi.setSchemeStatus(scheme.id, !scheme.active);
      await load();
    } catch {
      setError('操作失败，请重试');
    }
  };

  const remove = async (scheme: PromotionSchemeDto): Promise<void> => {
    if (!window.confirm(`确定删除促销方案「${scheme.title}」吗？`)) return;
    try {
      await catalogApi.deleteScheme(scheme.id);
      await load();
    } catch (e) {
      // 409 是「已被订单行引用」这一种业务冲突：契约 §8.3 要求改用停用而不是硬删
      const meta = readErrorMeta(e);
      setError(
        meta?.status === 409 || meta?.code === 'SCHEME_IN_USE'
          ? '该方案已被订单引用，请改用停用'
          : '删除失败，请重试',
      );
    }
  };

  const patchRow = (index: number, patch: Partial<SchemeRow>): void => {
    setRows((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  return (
    <div>
      <h1 className="mb-6 text-2xl font-bold text-gray-900">促销方案</h1>

      <div className="mb-4 flex items-center justify-end">
        {canWrite && <Button onClick={openCreate}>+ 新建促销方案</Button>}
      </div>

      {loading && <p className="py-8 text-center text-gray-500">加载中…</p>}
      {!loading && error && <p className="font-bold text-red-600">{error}</p>}

      {!loading && !error && schemes.length === 0 && (
        <p className="text-gray-500">还没有促销方案，点击「新建促销方案」创建</p>
      )}

      {!loading && !error && schemes.length > 0 && (
        <table className="w-full rounded-lg border-2 border-black">
          <thead className="bg-gray-50">
            <tr>
              <th className="border-b-2 border-black px-4 py-3 text-left font-bold">方案标题</th>
              <th className="border-b-2 border-black px-4 py-3 text-left font-bold">方案内商品</th>
              <th className="border-b-2 border-black px-4 py-3 text-left font-bold">原价合计</th>
              <th className="border-b-2 border-black px-4 py-3 text-left font-bold">方案价</th>
              <th className="border-b-2 border-black px-4 py-3 text-left font-bold">省额</th>
              <th className="border-b-2 border-black px-4 py-3 text-left font-bold">状态</th>
              {canWrite && (
                <th className="border-b-2 border-black px-4 py-3 text-left font-bold">操作</th>
              )}
            </tr>
          </thead>
          <tbody>
            {schemes.map((scheme) => (
              <tr key={scheme.id}>
                <td className="border-b border-gray-200 px-4 py-3 text-sm">{scheme.title}</td>
                <td className="border-b border-gray-200 px-4 py-3 text-sm">
                  {scheme.items.map((item) => `${item.productName} ×${item.quantity}`).join('、')}
                </td>
                <td className="border-b border-gray-200 px-4 py-3 text-sm text-gray-500 line-through">
                  {formatCents(scheme.originalTotalCents)}
                </td>
                <td className="border-b border-gray-200 px-4 py-3 text-sm font-bold">
                  {formatCents(scheme.priceCents)}
                </td>
                <td className="border-b border-gray-200 px-4 py-3 text-sm text-green-600">
                  {formatCents(scheme.discountCents)}
                </td>
                <td className="border-b border-gray-200 px-4 py-3 text-sm">
                  {scheme.active ? '启用' : '停用'}
                </td>
                {canWrite && (
                  <td className="border-b border-gray-200 px-4 py-3 text-sm">
                    <div className="flex gap-3">
                      <button
                        onClick={() => openEdit(scheme)}
                        className="font-medium text-blue-600 hover:text-blue-800"
                      >
                        编辑
                      </button>
                      <button
                        onClick={() => void toggleActive(scheme)}
                        className="font-medium text-gray-600 hover:text-gray-900"
                      >
                        {scheme.active ? '停用' : '启用'}
                      </button>
                      <button
                        onClick={() => void remove(scheme)}
                        className="font-medium text-red-600 hover:text-red-800"
                      >
                        删除
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <Modal
        open={showForm}
        onClose={() => setShowForm(false)}
        title={editing ? '编辑促销方案' : '新建促销方案'}
        maxWidth="max-w-2xl"
      >
        <div className="flex flex-col gap-3">
          {formError && (
            <p className="rounded-lg border-2 border-red-500 bg-red-50 p-2 text-sm text-red-600">
              {formError}
            </p>
          )}

          <Input
            label="方案标题"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />

          <label className="flex flex-col gap-1">
            <span className="text-sm font-bold">说明</span>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="min-h-[72px] rounded-lg border-2 border-black bg-white px-3 py-2 text-base outline-none focus:ring-2 focus:ring-black"
            />
          </label>

          <div className="flex flex-col gap-2">
            <span className="text-sm font-bold">方案内商品</span>
            {rows.map((row, index) => (
              <div key={index} className="flex items-center gap-3">
                <select
                  value={row.productId}
                  onChange={(e) => patchRow(index, { productId: e.target.value })}
                  className="min-h-[44px] flex-1 rounded-lg border-2 border-black bg-white px-3 text-base outline-none focus:ring-2 focus:ring-black"
                >
                  <option value="">请选择商品</option>
                  {products.map((product) => (
                    <option key={product.id} value={product.id}>
                      {product.name}（{formatServerYuan(product.price) ?? PRICE_UNAVAILABLE_TEXT}）
                    </option>
                  ))}
                </select>
                <input
                  type="number"
                  min={1}
                  value={row.quantity}
                  onChange={(e) => patchRow(index, { quantity: Number(e.target.value) })}
                  className="min-h-[44px] w-24 rounded-lg border-2 border-black bg-white px-3 text-center text-base outline-none focus:ring-2 focus:ring-black"
                />
                <button
                  onClick={() => setRows((prev) => prev.filter((_, i) => i !== index))}
                  disabled={rows.length <= 1}
                  className="font-medium text-red-600 hover:text-red-800 disabled:opacity-30"
                >
                  移除
                </button>
              </div>
            ))}
            {itemsError && <p className="text-sm font-bold text-red-600">{itemsError}</p>}
            <div>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setRows((prev) => [...prev, { ...EMPTY_ROW }])}
              >
                添加商品
              </Button>
            </div>
          </div>

          <Input
            label="方案价（元）"
            value={priceInput}
            inputMode="decimal"
            onChange={(e) => setPriceInput(e.target.value)}
          />
          {priceError && <p className="text-sm font-bold text-red-600">{priceError}</p>}

          <div className="rounded-lg border-2 border-black bg-gray-50 p-3 text-sm">
            <div className="flex justify-between">
              <span>原价合计</span>
              <span className="text-gray-500 line-through">
                {formatCents(preview.originalTotalCents)}
              </span>
            </div>
            <div className="mt-1 flex justify-between">
              <span>优惠额</span>
              <span className="text-green-600">{formatCents(preview.discountCents)}</span>
            </div>
            <div className="mt-1 flex justify-between">
              <span>每件优惠</span>
              <span>{formatCents(preview.perItemDiscountCents)}</span>
            </div>
            <div className="mt-1 flex justify-between font-bold">
              <span>实付合计</span>
              <span>{formatCents(preview.payableTotalCents)}</span>
            </div>
          </div>

          <label className="flex min-h-[44px] items-center gap-3 text-sm font-bold">
            <input
              type="checkbox"
              className="h-5 w-5"
              checked={active}
              onChange={(e) => setActive(e.target.checked)}
            />
            启用
          </label>

          <div className="mt-2 flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setShowForm(false)} disabled={saving}>
              取消
            </Button>
            <Button onClick={() => void submit()} disabled={saving} className="disabled:opacity-50">
              {saving ? '保存中…' : '保存'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
